import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';

import { EnterprisePage } from '../components/layout/EnterprisePage';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import { ProblemCaseList } from '../components/problemas/ProblemCaseList';
import { EnterpriseModal, ModalField, modalInputClass, modalSelectClass, ModalPrimaryButton, ModalSecondaryButton } from '../components/layout/EnterpriseModal';
import { KanbanBoard } from '../components/kanban/KanbanBoard';
import type { KanbanColumnConfig, KanbanCardData } from '../components/kanban/KanbanBoard';
import { ProblemSummaryPanel } from '../components/problemas/ProblemSummaryPanel';
import { ProblemExecutiveStrip } from '../components/problemas/ProblemExecutiveStrip';
import { buildProblemDashboard, filterByQuickFilter, type ProblemQuickFilter } from '../services/problemDashboardData';
import { resolveIssueMode } from '../utils/resolveIssueMode';
import { useProblemasRibbon } from '../hooks/useProblemasRibbon';
import { useAppToast } from '../components/AppToast';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useExecutionSelection } from '../contexts/ExecutionSelectionContext';
import { useWorkspaceProjectFilter } from '../context/ProjectContext';
import { ProjectService } from '../services/projectService';
import { WorkspaceProjectScopeBar } from '../components/workspace/WorkspaceProjectScopeBar';
import { IssueService } from '../services/issueService';
import { normalizeRequiredReason } from '../utils/problemReason';
import { ProblemReasonModal } from '../components/problemas/ProblemReasonModal';
import { buildProblemDraft, parseProblemDraft, type OperationalOrigin } from '../components/ops/exceptionLoop';
import type { IssueFilters } from '../services/issueService';
import { useOrgMembers } from '../hooks/useOrgMembers';
import { resolveIssueAssigneeName } from '../utils/issueAssignee';
import type { StatusCapsuleItem } from '../components/layout/StatusCapsule';
import {
  ISSUE_STATUS_LABELS, ISSUE_TYPE_LABELS, ISSUE_SEVERITY_LABELS,
  transitionRefusal,
} from '../types/issue';
import type { Issue, IssueStatus, IssueType, IssueSeverity, IssueMode, RecurrenceMatch } from '../types/issue';
import { exportToCSV, exportToPrintPDF } from '../utils/exportTableData';
import {
  BarChart3, X, ChevronDown, ChevronUp, Check,
  AlertCircle, Search, Crosshair, Clock, CheckCircle2, Shield, Eye, PlayCircle, ClipboardCheck,
  User, Calendar, LinkIcon, Folder, Plus,
} from 'lucide-react';

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-600 border border-red-500/20 dark:text-red-400',
  high: 'bg-orange-500/10 text-orange-600 border border-orange-500/20 dark:text-orange-400',
  medium: 'bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400',
  low: 'bg-slate-500/10 text-slate-600 border border-slate-500/20 dark:text-slate-400',
};

const STATUS_COLOR: Record<string, string> = {
  open: 'bg-red-500/10 text-red-600 border border-red-500/20 dark:text-red-400',
  investigation: 'bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400',
  root_cause: 'bg-orange-500/10 text-orange-600 border border-orange-500/20 dark:text-orange-400',
  action_plan: 'bg-blue-500/10 text-blue-600 border border-blue-500/20 dark:text-blue-400',
  execution: 'bg-indigo-500/10 text-indigo-600 border border-indigo-500/20 dark:text-indigo-400',
  validation: 'bg-purple-500/10 text-purple-600 border border-purple-500/20 dark:text-purple-400',
  monitoring: 'bg-cyan-500/10 text-cyan-600 border border-cyan-500/20 dark:text-cyan-400',
  closed: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 dark:text-emerald-400',
  cancelled: 'bg-slate-500/10 text-slate-500 border border-slate-500/20 dark:text-slate-400',
};

const KANBAN_STATUSES: IssueStatus[] = ['open', 'investigation', 'root_cause', 'action_plan', 'execution', 'validation', 'monitoring', 'closed'];

const KANBAN_COLUMNS: KanbanColumnConfig[] = [
  { id: 'open', title: 'Aberto', icon: AlertCircle, headerBorder: 'border-t-red-500', badgeColor: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  { id: 'investigation', title: 'Investigação', icon: Search, headerBorder: 'border-t-amber-500', badgeColor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { id: 'root_cause', title: 'Causa Raiz', icon: Crosshair, headerBorder: 'border-t-orange-500', badgeColor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { id: 'action_plan', title: 'Plano de Ação', icon: ClipboardCheck, headerBorder: 'border-t-blue-500', badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { id: 'execution', title: 'Execução', icon: PlayCircle, headerBorder: 'border-t-indigo-500', badgeColor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  { id: 'validation', title: 'Validação', icon: Shield, headerBorder: 'border-t-purple-500', badgeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { id: 'monitoring', title: 'Monitoramento', icon: Eye, headerBorder: 'border-t-cyan-500', badgeColor: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { id: 'closed', title: 'Encerrado', icon: CheckCircle2, headerBorder: 'border-t-emerald-500', badgeColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
];

const EXPORT_COLUMNS = [
  { key: 'title', label: 'Título' },
  { key: 'type', label: 'Tipo' },
  { key: 'severity', label: 'Severidade' },
  { key: 'status', label: 'Status' },
  { key: 'assigned_to', label: 'Responsável' },
  { key: 'created_at', label: 'Criado em' },
];

/**
 * 🚨 ProblemasPage — Gestão de Problemas e Não-Conformidades
 */
export const ProblemasPage: React.FC = () => {
  const organizationId = useOrganizationId();
  const {
    filterProjectId,
    filterProjectName,
    isProjectScopeActive,
    focusWorkspaceProject,
    dismissProjectScope,
  } = useWorkspaceProjectFilter();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPlanId = searchParams.get('planId');
  const { selectedProblemId, selectProblem } = useExecutionSelection();
  const { showToast, ToastContainer } = useAppToast();
  const { user } = useAuth();

  const { members: orgMembers } = useOrgMembers(organizationId);

  const resolveAssignee = useCallback(
    (assignedTo: string | null | undefined) => resolveIssueAssigneeName(assignedTo, orgMembers),
    [orgMembers],
  );

  // ── UI State ───────────────────────────────────────
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [reasonAsk, setReasonAsk] = useState<null | { mode: 'close' | 'reopen'; issueId: string }>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [pendingOrigin, setPendingOrigin] = useState<OperationalOrigin | null>(null);
  const draftAppliedRef = useRef(false);

  // ── Create form state ──────────────────────────────
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState<IssueType>('incident');
  const [newSeverity, setNewSeverity] = useState<IssueSeverity>('medium');
  const [newDescription, setNewDescription] = useState('');
  const [newDepartment, setNewDepartment] = useState('');
  const [forceStructured, setForceStructured] = useState(false);
  const [quickFilter, setQuickFilter] = useState<ProblemQuickFilter | null>(null);

  // ── Filter state ───────────────────────────────────
  const [filterStatus, setFilterStatus] = useState<IssueStatus | ''>('');
  const [filterType, setFilterType] = useState<IssueType | ''>('');
  const [filterSeverity, setFilterSeverity] = useState<IssueSeverity | ''>('');
  const [filterSearch, setFilterSearch] = useState('');
  const hasActiveFilters = Boolean(filterStatus || filterType || filterSeverity || filterSearch);

  const { data: enterpriseProjects = [] } = useQuery({
    queryKey: ['enterprise-projects-list', organizationId],
    queryFn: () => ProjectService.list(organizationId),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  // ── Filter dropdown state ──────────────────────────
  type FilterDropdown = 'status' | 'type' | 'severity' | 'projeto' | null;
  const [openFilterDropdown, setOpenFilterDropdown] = useState<FilterDropdown>(null);
  const projetoBtnRef = useRef<HTMLButtonElement>(null);
  const [projetoPos, setProjetoPos] = useState({ top: 0, left: 0 });
  const statusBtnRef = useRef<HTMLButtonElement>(null);
  const typeBtnRef = useRef<HTMLButtonElement>(null);
  const severityBtnRef = useRef<HTMLButtonElement>(null);
  const contentExportRef = useRef<HTMLDivElement>(null);
  const [statusPos, setStatusPos] = useState({ top: 0, left: 0 });
  const [typePos, setTypePos] = useState({ top: 0, left: 0 });
  const [severityPos, setSeverityPos] = useState({ top: 0, left: 0 });

  const openFilterDd = useCallback((which: FilterDropdown) => {
    const refMap = { status: statusBtnRef, type: typeBtnRef, severity: severityBtnRef, projeto: projetoBtnRef };
    const setMap = { status: setStatusPos, type: setTypePos, severity: setSeverityPos, projeto: setProjetoPos };
    if (!which) return;
    const ref = refMap[which];
    const setPos = setMap[which];
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenFilterDropdown(prev => prev === which ? null : which);
  }, []);

  useEffect(() => {
    if (!openFilterDropdown) return;
    const close = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-problems-btn]') || target.closest('[data-problems-dropdown]')) return;
      setOpenFilterDropdown(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openFilterDropdown]);

  // Auto-select from URL ?id= or global context
  useEffect(() => {
    const urlId = searchParams.get('id');
    const targetId = urlId ?? selectedProblemId;
    if (targetId && !selectedIssueId) {
      setSelectedIssueId(targetId);
      setShowDetailPanel(true);
      if (selectedProblemId) selectProblem(null); // consume
    }
  }, [searchParams, selectedProblemId, selectedIssueId, selectProblem]);

  const queryFilters = useMemo<IssueFilters>(() => ({
    project_id: filterProjectId ?? undefined,
    ...(urlPlanId && { plan_id: urlPlanId }),
    ...(filterStatus && { status: filterStatus as IssueStatus }),
    ...(filterType && { type: filterType as IssueType }),
    ...(filterSeverity && { severity: filterSeverity as IssueSeverity }),
  }), [filterProjectId, urlPlanId, filterStatus, filterType, filterSeverity]);

  const issuesQuery = useQuery({
    queryKey: ['issues', organizationId, filterProjectId, urlPlanId, queryFilters],
    enabled: Boolean(organizationId),
    queryFn: () => IssueService.list(organizationId, queryFilters),
    staleTime: 30_000,
  });

  const issuesRaw = issuesQuery.data ?? [];
  const dashboardData = useMemo(() => buildProblemDashboard(issuesRaw), [issuesRaw]);
  const issues = useMemo(() => {
    let list = dashboardData.issues;
    if (quickFilter) list = filterByQuickFilter(list, quickFilter);
    if (!filterSearch.trim()) return list;
    const q = filterSearch.toLowerCase();
    return list.filter(i =>
      i.title?.toLowerCase().includes(q) ||
      i.description?.toLowerCase().includes(q) ||
      i.code?.toLowerCase().includes(q)
    );
  }, [dashboardData.issues, quickFilter, filterSearch]);
  const selectedIssue = issues.find(i => i.id === selectedIssueId) ?? null;

  const [recurrences, setRecurrences] = useState<RecurrenceMatch[]>([]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['issues'] });
    queryClient.invalidateQueries({ queryKey: ['issue-alerts'] });
    queryClient.invalidateQueries({ queryKey: ['ops-problems'] });
    queryClient.invalidateQueries({ queryKey: ['ops-metrics'] });
  }, [queryClient]);

  // ── Command: issue.create (modal) ─────────────────
  const clearProblemDraft = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    [
      'draft', 'exception_type', 'origin_label', 'return_path', 'detected_at', 'signal_label',
      'stage', 'department_id', 'department_name', 'workflow_id', 'execution_id', 'task_id',
      'project_id', 'action_id', 'period_label', 'wip', 'minutes',
    ].forEach((key) => next.delete(key));
    setPendingOrigin(null);
    draftAppliedRef.current = false;
    navigate({ pathname: '/problemas', search: next.toString() }, { replace: true });
  }, [navigate, searchParams]);

  const handleOpenCreateModal = useCallback(() => {
    setPendingOrigin(null);
    setNewTitle('');
    setNewType('incident');
    setNewSeverity('medium');
    setNewDescription('');
    setNewDepartment('');
    setForceStructured(false);
    setRecurrences([]);
    setShowCreateModal(true);
  }, []);

  useEffect(() => {
    const origin = parseProblemDraft(searchParams.toString());
    if (!origin) {
      draftAppliedRef.current = false;
      return;
    }
    if (draftAppliedRef.current) return;
    const draft = buildProblemDraft(origin);
    draftAppliedRef.current = true;
    setPendingOrigin(origin);
    setNewTitle(draft.title);
    setNewType('operational_failure');
    setNewSeverity('high');
    setNewDescription(draft.description);
    setNewDepartment(draft.department ?? '');
    setForceStructured(false);
    setRecurrences([]);
    setShowCreateModal(true);
  }, [searchParams]);

  // ── Recurrence check before create ────────────────
  const handleCheckRecurrence = useCallback(async () => {
    if (!newType) return;
    try {
      const matches = await IssueService.detectRecurrence(organizationId, {
        type: newType,
        root_cause: newDescription.trim() || null,
      });
      setRecurrences(matches);
    } catch {
      // Non-blocking — silent fail
    }
  }, [organizationId, newType, newDescription]);

  const handleSubmitCreate = useCallback(async () => {
    if (!newTitle.trim()) {
      showToast('warning', 'Informe o título do problema');
      return;
    }
    setCreateLoading(true);
    try {
      // Check recurrence before creating
      const matches = await IssueService.detectRecurrence(organizationId, {
        type: newType,
        root_cause: newDescription.trim() || null,
      });
      const recurrenceDetected = matches.length > 0;
      if (recurrenceDetected) {
        setRecurrences(matches);
      }

      const mode = resolveIssueMode({
        type: newType,
        severity: newSeverity,
        recurrenceDetected,
        forceStructured,
      });

      const originDraft = pendingOrigin ? buildProblemDraft(pendingOrigin) : null;
      const storedOrigin = originDraft
        ? {
            ...originDraft.metadata.operational_origin,
            organization_id: originDraft.metadata.operational_origin.organization_id ?? organizationId,
          }
        : null;
      await IssueService.create(organizationId, {
        title: newTitle.trim(),
        type: newType,
        severity: newSeverity,
        description: newDescription.trim() || null,
        status: 'open',
        mode,
        department: newDepartment.trim() || originDraft?.department || null,
        step: originDraft?.step ?? null,
        workflow_id: originDraft?.workflow_id ?? null,
        source: originDraft?.source ?? 'manual',
        metadata: {
          recurrence_detected: recurrenceDetected,
          force_structured: forceStructured,
          ...(storedOrigin ? { operational_origin: storedOrigin } : {}),
        },
      }, { recurrenceDetected, forceStructured });
      showToast('success', 'Problema criado com sucesso');
      setShowCreateModal(false);
      setPendingOrigin(null);
      clearProblemDraft();
      handleRefresh();
    } catch (err) {
      console.error('[Issues] create failed:', err);
      showToast('error', 'Erro ao criar problema');
    } finally {
      setCreateLoading(false);
    }
  }, [organizationId, newTitle, newType, newSeverity, newDescription, forceStructured, newDepartment, pendingOrigin, clearProblemDraft, handleRefresh, showToast]);

  // ── Command: issue.close ───────────────────────────
  const handleCloseIssue = useCallback(async () => {
    if (!selectedIssueId) return;
    const stored = normalizeRequiredReason(selectedIssue?.metadata?.closure_reason);
    if (!stored) {
      setReasonAsk({ mode: 'close', issueId: selectedIssueId });
      return;
    }
    try {
      await IssueService.transitionStatus(organizationId, selectedIssueId, 'closed', {
        actorId: user?.id,
        actorName: user?.name,
      });
      showToast('success', 'Problema encerrado');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] close failed:', err);
      showToast('error', err instanceof Error ? err.message : 'Erro ao encerrar problema');
    }
  }, [organizationId, selectedIssueId, selectedIssue, handleRefresh, showToast, user?.id, user?.name]);



  // ── Command: issue.validate ────────────────────────
  const handleValidate = useCallback(async () => {
    if (!selectedIssueId) return;
    try {
      await IssueService.transitionStatus(organizationId, selectedIssueId, 'validation');
      showToast('info', 'Validação iniciada');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] validate failed:', err);
      showToast('error', 'Erro ao iniciar validação');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast]);

  // ── Command: issue.monitor ─────────────────────────
  const handleMonitor = useCallback(async () => {
    if (!selectedIssueId) return;
    try {
      await IssueService.transitionStatus(organizationId, selectedIssueId, 'monitoring');
      showToast('info', 'Monitoramento iniciado');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] monitor failed:', err);
      showToast('error', 'Erro ao iniciar monitoramento');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast]);

  // ── Command: issue.cancel ──────────────────────────
  const handleCancel = useCallback(async () => {
    if (!selectedIssueId || !selectedIssue) return;
    if (selectedIssue.status !== 'closed') {
      showToast('error', transitionRefusal(selectedIssue.mode, selectedIssue.status, 'open'));
      return;
    }
    setReasonAsk({ mode: 'reopen', issueId: selectedIssueId });
    return;
  }, [organizationId, selectedIssueId, selectedIssue, showToast]);

  const confirmReasonAsk = useCallback(async (raw: string) => {
    const reason = normalizeRequiredReason(raw);
    if (!reason || !reasonAsk) return;
    const ask = reasonAsk;
    setReasonAsk(null);
    try {
      if (ask.mode === 'reopen') {
        await IssueService.transitionStatus(organizationId, ask.issueId, 'open', {
          reopenReason: reason,
          actorId: user?.id,
          actorName: user?.name,
        });
        showToast('success', 'Problema reaberto');
      } else {
        await IssueService.transitionStatus(organizationId, ask.issueId, 'closed', {
          closureReason: reason,
          actorId: user?.id,
          actorName: user?.name,
        });
        showToast('success', 'Problema encerrado');
      }
      handleRefresh();
    } catch (err) {
      console.error('[Issues] reason failed:', err);
      showToast('error', err instanceof Error ? err.message : 'Erro ao registrar o motivo');
    }
  }, [organizationId, reasonAsk, handleRefresh, showToast, user?.id, user?.name]);

  // ── Command: issue.assign (assume responsibility) ──
  const handleAssign = useCallback(async () => {
    if (!selectedIssueId) return;
    if (!user?.id) {
      showToast('error', 'Não foi possível identificar o usuário.');
      return;
    }
    try {
      await IssueService.update(organizationId, selectedIssueId, { assigned_to: user.id }, {
        actorId: user.id,
        actorName: user.name,
        historyType: 'assigned',
        historyMessage: user.name ? `Responsável: ${user.name}` : 'Responsável definido',
      });
      showToast('success', 'Problema assumido');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] assign failed:', err);
      showToast('error', 'Erro ao assumir problema');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast, user?.id, user?.name]);

  // ── Command: issue.createLinkedAction ───────────────
  const handleCreateLinkedAction = useCallback(() => {
    if (!selectedIssue) return;
    navigate('/acoes/nova', {
      state: {
        fromIssue: {
          id: selectedIssue.id,
          title: selectedIssue.title,
          severity: selectedIssue.severity,
          description: selectedIssue.description,
        },
      },
    });
  }, [selectedIssue, navigate]);

  // ── Command: export.pdf ────────────────────────────
  const handleExportPDF = useCallback(() => {
    const exportData = issues.map(i => ({
      title: i.title,
      type: ISSUE_TYPE_LABELS[i.type],
      severity: ISSUE_SEVERITY_LABELS[i.severity],
      status: ISSUE_STATUS_LABELS[i.status],
      assigned_to: resolveAssignee(i.assigned_to),
      created_at: new Date(i.created_at).toLocaleDateString('pt-BR'),
    }));
    exportToPrintPDF(exportData, EXPORT_COLUMNS, 'Gestão de Problemas');
    showToast('info', 'PDF gerado para impressão');
  }, [issues, showToast, resolveAssignee]);

  const handleExportCSV = useCallback(() => {
    const exportData = issues.map(i => ({
      title: i.title,
      type: ISSUE_TYPE_LABELS[i.type],
      severity: ISSUE_SEVERITY_LABELS[i.severity],
      status: ISSUE_STATUS_LABELS[i.status],
      assigned_to: resolveAssignee(i.assigned_to),
      created_at: new Date(i.created_at).toLocaleDateString('pt-BR'),
    }));
    exportToCSV(exportData, EXPORT_COLUMNS, 'problemas');
    showToast('success', 'Planilha CSV exportada');
  }, [issues, showToast, resolveAssignee]);

  const handleExportPNG = useCallback(async () => {
    const el = contentExportRef.current;
    if (!el) {
      showToast('error', 'Área de exportação não encontrada');
      return;
    }
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
      canvas.toBlob((blob) => {
        if (!blob) {
          showToast('error', 'Falha ao gerar imagem');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `problemas_${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast('success', 'PNG exportado');
      }, 'image/png');
    } catch {
      showToast('error', 'Falha ao exportar PNG');
    }
  }, [showToast]);

  const handleSaveView = useCallback(() => {
    if (!organizationId) return;
    const payload = {
      viewMode,
      showDashboard,
      filterStatus,
      filterType,
      filterSeverity,
      filterSearch,
    };
    try {
      localStorage.setItem(`wf_problemas_view_${organizationId}`, JSON.stringify(payload));
      showToast('success', 'Preferências de visualização salvas');
    } catch {
      showToast('error', 'Não foi possível salvar as preferências');
    }
  }, [organizationId, viewMode, showDashboard, filterStatus, filterType, filterSeverity, filterSearch, showToast]);

  const handleSaveAsWf = useCallback(() => {
    const snapshot = {
      appSignature: 'WORKFLOW_STUDIO',
      version: '1.0',
      timestamp: new Date().toISOString(),
      module: 'issues',
      organizationId,
      projectId: filterProjectId ?? null,
      issues,
      filters: { filterStatus, filterType, filterSeverity, filterSearch },
    };
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `problemas_${new Date().toISOString().split('T')[0]}.wf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('success', 'Arquivo .wf exportado');
  }, [organizationId, filterProjectId, issues, filterStatus, filterType, filterSeverity, filterSearch, showToast]);

  // ── Command: filter toggle ─────────────────────────
  const handleClearFilters = useCallback(() => {
    setFilterStatus('');
    setFilterType('');
    setFilterSeverity('');
    setFilterSearch('');
  }, []);

  const handleDismissProjectScope = useCallback(() => {
    dismissProjectScope();
    showToast('info', 'Filtro de projeto removido. Exibindo todos os problemas.');
  }, [dismissProjectScope, showToast]);

  // ── Command: dashboard toggle ──────────────────────
  const handleToggleDashboard = useCallback(() => {
    setShowDashboard(prev => !prev);
  }, []);

  // ── Ribbon (hook-based MasterRibbon) ────────────
  const ribbonConfig = useProblemasRibbon({
    selectedIssue,
    viewMode,
    showDashboard,
    hasActiveFilters,
    onNewIssue: handleOpenCreateModal,
    onAssign: handleAssign,
    onCreateLinkedAction: handleCreateLinkedAction,
    onValidate: handleValidate,
    onMonitor: handleMonitor,
    onCloseIssue: handleCloseIssue,
    onCancelIssue: handleCancel,
    onViewModeChange: setViewMode,
    onToggleDashboard: handleToggleDashboard,
    onSave: handleSaveView,
    onSaveAsWf: handleSaveAsWf,
    onExportPDF: handleExportPDF,
    onExportPNG: handleExportPNG,
    onExportCSV: handleExportCSV,
  });

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const dropdownPanelClass = 'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const dropdownItemClass = 'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const activeStatusLabel = filterStatus ? ISSUE_STATUS_LABELS[filterStatus] : 'Todos';
  const activeTypeLabel = filterType ? ISSUE_TYPE_LABELS[filterType] : 'Todos';
  const activeSeverityLabel = filterSeverity ? ISSUE_SEVERITY_LABELS[filterSeverity] : 'Todas';
  const activeProjetoLabel = isProjectScopeActive
    ? (filterProjectName ?? 'Projeto ativo')
    : 'Todos';

  const filtersWidget = useMemo(() => (
    <div className="flex w-full min-w-0 items-center gap-1.5">
      {isProjectScopeActive && (
        <button
          type="button"
          onClick={handleDismissProjectScope}
          className="flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-blue-300/50 bg-blue-500/10 px-2 text-[11px] text-blue-700 transition-colors hover:bg-blue-500/15 dark:border-blue-500/30 dark:text-blue-300"
          title={`Projeto ativo: ${filterProjectName ?? 'Projeto'}. Clique para ver todos.`}
        >
          <Folder size={12} />
          <span className="max-w-[140px] truncate">{filterProjectName ?? 'Projeto'}</span>
          <span className="opacity-80">• Ver todos</span>
        </button>
      )}

      {/* Projeto */}
      <button ref={projetoBtnRef} data-problems-btn="" type="button" className={filterBtnClass} onClick={() => openFilterDd('projeto')}>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Projeto</span>
        <span className="max-w-[120px] truncate">{activeProjetoLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDropdown === 'projeto' && createPortal(
        <div data-problems-dropdown="" className={dropdownPanelClass} style={{ position: 'fixed', top: projetoPos.top, left: projetoPos.left }}>
          <button type="button" className={dropdownItemClass} onClick={() => { handleDismissProjectScope(); setOpenFilterDropdown(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos os projetos</span>
            <Check size={11} className={!isProjectScopeActive ? 'opacity-100' : 'opacity-0'} />
          </button>
          {enterpriseProjects.length > 0 && (
            <>
              <div className="my-1 border-t border-[var(--border-default)]" />
              {enterpriseProjects.map(project => (
                <button
                  key={project.id}
                  type="button"
                  className={dropdownItemClass}
                  onClick={() => {
                    focusWorkspaceProject(project.id, project.name);
                    setOpenFilterDropdown(null);
                  }}
                >
                  <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full bg-blue-500" />
                  <span className="flex-1 truncate">{project.name}</span>
                  <Check size={11} className={filterProjectId === project.id ? 'opacity-100' : 'opacity-0'} />
                </button>
              ))}
            </>
          )}
        </div>,
        document.body
      )}

      {/* Status */}
      <button ref={statusBtnRef} data-problems-btn="" type="button" className={filterBtnClass} onClick={() => openFilterDd('status')}>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Status</span>
        <span>{activeStatusLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDropdown === 'status' && createPortal(
        <div data-problems-dropdown="" className={dropdownPanelClass} style={{ position: 'fixed', top: statusPos.top, left: statusPos.left }}>
          <button type="button" className={dropdownItemClass} onClick={() => { setFilterStatus(''); setOpenFilterDropdown(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos</span>
            <Check size={11} className={!filterStatus ? 'opacity-100' : 'opacity-0'} />
          </button>
          {Object.entries(ISSUE_STATUS_LABELS).map(([k, v]) => (
            <button key={k} type="button" className={dropdownItemClass} onClick={() => { setFilterStatus(k as IssueStatus); setOpenFilterDropdown(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: k === 'open' ? '#ef4444' : k === 'closed' ? '#22c55e' : '#3b82f6' }} />
              <span className="flex-1">{v}</span>
              <Check size={11} className={filterStatus === k ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Type */}
      <button ref={typeBtnRef} data-problems-btn="" type="button" className={filterBtnClass} onClick={() => openFilterDd('type')}>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Tipo</span>
        <span>{activeTypeLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDropdown === 'type' && createPortal(
        <div data-problems-dropdown="" className={dropdownPanelClass} style={{ position: 'fixed', top: typePos.top, left: typePos.left }}>
          <button type="button" className={dropdownItemClass} onClick={() => { setFilterType(''); setOpenFilterDropdown(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos</span>
            <Check size={11} className={!filterType ? 'opacity-100' : 'opacity-0'} />
          </button>
          {Object.entries(ISSUE_TYPE_LABELS).map(([k, v]) => (
            <button key={k} type="button" className={dropdownItemClass} onClick={() => { setFilterType(k as IssueType); setOpenFilterDropdown(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{v}</span>
              <Check size={11} className={filterType === k ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Severity */}
      <button ref={severityBtnRef} data-problems-btn="" type="button" className={filterBtnClass} onClick={() => openFilterDd('severity')}>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Severidade</span>
        <span>{activeSeverityLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDropdown === 'severity' && createPortal(
        <div data-problems-dropdown="" className={dropdownPanelClass} style={{ position: 'fixed', top: severityPos.top, left: severityPos.left }}>
          <button type="button" className={dropdownItemClass} onClick={() => { setFilterSeverity(''); setOpenFilterDropdown(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todas</span>
            <Check size={11} className={!filterSeverity ? 'opacity-100' : 'opacity-0'} />
          </button>
          {Object.entries(ISSUE_SEVERITY_LABELS).map(([k, v]) => (
            <button key={k} type="button" className={dropdownItemClass} onClick={() => { setFilterSeverity(k as IssueSeverity); setOpenFilterDropdown(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: k === 'critical' ? '#ef4444' : k === 'high' ? '#f97316' : k === 'medium' ? '#3b82f6' : '#94a3b8' }} />
              <span className="flex-1">{v}</span>
              <Check size={11} className={filterSeverity === k ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Search */}
      <div className="ml-auto flex w-[min(280px,34%)] min-w-[140px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar problema..."
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {filterSearch && (
            <button onClick={() => setFilterSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [
    filterStatus, filterType, filterSeverity, filterSearch, openFilterDropdown,
    statusPos, typePos, severityPos, projetoPos, activeStatusLabel, activeTypeLabel,
    activeSeverityLabel, activeProjetoLabel, openFilterDd, handleClearFilters,
    isProjectScopeActive, filterProjectId, filterProjectName, enterpriseProjects,
    handleDismissProjectScope, focusWorkspaceProject,
  ]);

  const ribbonWithFilters = ribbonConfig;

  // ── Dashboard stats ────────────────────────────────
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of KANBAN_STATUSES) counts[s] = 0;
    for (const i of issues) counts[i.status] = (counts[i.status] || 0) + 1;
    return counts;
  }, [issues]);

  const severityCounts = useMemo(() => {
    const counts: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const i of issues) counts[i.severity] = (counts[i.severity] || 0) + 1;
    return counts;
  }, [issues]);

  const avgResolutionDays = useMemo(() => {
    const closed = issues.filter(i => i.status === 'closed' && i.resolved_at);
    if (closed.length === 0) return null;
    const total = closed.reduce((acc, i) => {
      const created = new Date(i.created_at).getTime();
      const resolved = new Date(i.resolved_at!).getTime();
      return acc + (resolved - created) / (1000 * 60 * 60 * 24);
    }, 0);
    return Math.round((total / closed.length) * 10) / 10;
  }, [issues]);

  const avgStageTimeDays = useMemo(() => {
    const resolved = issues.filter(i => i.status === 'closed' && i.resolved_at);
    if (resolved.length === 0) return null;
    const stageCount = 7; // open → investigation → root_cause → action_plan → execution → validation → monitoring → closed
    const totalDays = resolved.reduce((acc, i) => {
      const created = new Date(i.created_at).getTime();
      const res = new Date(i.resolved_at!).getTime();
      return acc + (res - created) / (1000 * 60 * 60 * 24);
    }, 0);
    return Math.round((totalDays / resolved.length / stageCount) * 10) / 10;
  }, [issues]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of Object.keys(ISSUE_TYPE_LABELS)) counts[t] = 0;
    for (const i of issues) counts[i.type] = (counts[i.type] || 0) + 1;
    return counts;
  }, [issues]);

  const departmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const i of issues) {
      const dept = (i as any).department || 'Não definido';
      counts[dept] = (counts[dept] || 0) + 1;
    }
    return counts;
  }, [issues]);

  const modeCounts = useMemo(() => {
    const counts = { quick_correction: 0, structured_problem: 0 };
    for (const i of issues) {
      const mode = (i as any).mode || 'quick_correction';
      if (mode in counts) counts[mode as keyof typeof counts]++;
    }
    return counts;
  }, [issues]);

  // ── Status capsules for PageHeader ─────────────────
  const CAPSULE_COLOR_MAP: Record<string, string> = {
    open: 'red', investigation: 'amber', root_cause: 'amber', action_plan: 'blue',
    execution: 'purple', validation: 'sky', monitoring: 'sky', closed: 'emerald',
  };
  const handleCapsuleClick = useCallback((status: IssueStatus) => {
    setFilterStatus(prev => prev === status ? '' : status);
  }, []);

  const scopeStats = useMemo(() => {
    const openCount = issues.filter(i => i.status === 'open' || i.status === 'investigation').length;
    const criticalCount = issues.filter(i => i.severity === 'critical').length;
    return { openCount, criticalCount };
  }, [issues]);

  const scopeBarTone = useMemo(() => {
    if (scopeStats.criticalCount > 0) return 'critical' as const;
    if (scopeStats.openCount > 0) return 'risk' as const;
    return 'healthy' as const;
  }, [scopeStats]);

  const scopeBarLabel = useMemo(() => {
    if (scopeStats.criticalCount > 0) return 'CRÍTICO';
    if (scopeStats.openCount > 0) return 'EM RISCO';
    return 'EM DIA';
  }, [scopeStats]);

  const problemasHeaderMeta = useMemo(() => {
    if (!isProjectScopeActive || !filterProjectName) return null;
    return (
      <button
        type="button"
        onClick={handleDismissProjectScope}
        title="Ver todos os problemas"
        className="max-w-[220px] truncate text-[12px] font-medium text-[var(--text-primary)] hover:underline hover:underline-offset-2"
      >
        {filterProjectName}
      </button>
    );
  }, [filterProjectName, handleDismissProjectScope, isProjectScopeActive]);

  const statusItems = useMemo<StatusCapsuleItem[]>(() =>
    (['open', 'investigation', 'execution', 'monitoring', 'closed'] as IssueStatus[]).map(s => ({
      label: ISSUE_STATUS_LABELS[s],
      value: statusCounts[s] ?? 0,
      color: CAPSULE_COLOR_MAP[s] ?? 'slate',
      onClick: () => handleCapsuleClick(s),
      active: filterStatus === s,
    })),
  [statusCounts, filterStatus, handleCapsuleClick]);

  // ── Kanban status change handler ────────────────────
  const handleKanbanStatusChange = useCallback(async (itemId: string, newStatus: string) => {
    try {
      const current = issues.find((item) => item.id === itemId);
      if (current?.status === 'closed' && newStatus === 'open') {
        setReasonAsk({ mode: 'reopen', issueId: itemId });
        return;
      }
      if (newStatus === 'closed') {
        const stored = normalizeRequiredReason(current?.metadata?.closure_reason);
        if (!stored) {
          setReasonAsk({ mode: 'close', issueId: itemId });
          return;
        }
      }
      await IssueService.transitionStatus(organizationId, itemId, newStatus as IssueStatus);
      handleRefresh();
    } catch (err) {
      console.error('[Issues] kanban status change failed:', err);
      showToast('error', err instanceof Error ? err.message : `Transição inválida para ${ISSUE_STATUS_LABELS[newStatus as IssueStatus] ?? newStatus}`);
    }
  }, [organizationId, handleRefresh, showToast, issues]);

  // ── Kanban card renderer ───────────────────────────
  const renderIssueCard = useCallback((issue: Issue & KanbanCardData) => {
    const isSelected = selectedIssueId === issue.id;
    const daysSinceCreation = Math.floor((Date.now() - new Date(issue.created_at).getTime()) / (1000 * 60 * 60 * 24));
    const issueMode = (issue as any).mode as IssueMode | undefined;
    const linkedCount = (issue as any).linked_actions_count as number | undefined;

    return (
      <button
        type="button"
        onClick={() => { setSelectedIssueId(issue.id); setShowDetailPanel(true); }}
        className={`w-full cursor-grab rounded border bg-white p-3 text-left transition active:cursor-grabbing dark:bg-slate-800/80 ${
          isSelected
            ? 'border-blue-400 ring-1 ring-blue-400 dark:border-blue-500'
            : 'border-slate-200 hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:hover:border-slate-600'
        }`}
      >
        {/* Mode indicator */}
        {issueMode === 'structured_problem' && (
          <div className="mb-1.5 flex items-center gap-1">
            <Crosshair size={9} className="text-orange-500" />
            <span className="text-[8px] font-semibold uppercase tracking-wider text-orange-600 dark:text-orange-400">MASP</span>
          </div>
        )}

        {/* Title */}
        <p className="line-clamp-2 text-[12px] font-medium text-slate-800 dark:text-slate-200">{issue.title}</p>

        {/* Badges row: severity + type */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <EnterpriseBadge label={ISSUE_SEVERITY_LABELS[issue.severity]} color={SEVERITY_COLOR[issue.severity]} />
          <span className="text-[9px] text-slate-400 dark:text-slate-500">{ISSUE_TYPE_LABELS[issue.type]}</span>
        </div>

        {/* Footer: responsible + time + actions */}
        <div className="mt-2.5 flex items-center gap-2 border-t border-slate-100 pt-2 dark:border-slate-700/60">
          {issue.assigned_to ? (
            <div className="flex items-center gap-1 text-[10px] text-slate-600 dark:text-slate-400">
              <User size={10} />
              <span className="max-w-[80px] truncate">{resolveAssignee(issue.assigned_to)}</span>
            </div>
          ) : (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">Sem resp.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {linkedCount != null && linkedCount > 0 && (
              <div className="flex items-center gap-0.5 text-[10px] text-blue-500 dark:text-blue-400" title={`${linkedCount} ação(ões) vinculada(s)`}>
                <LinkIcon size={9} />
                <span>{linkedCount}</span>
              </div>
            )}
            <div className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500" title={`Aberto há ${daysSinceCreation} dia(s)`}>
              <Clock size={10} />
              <span>{daysSinceCreation}d</span>
            </div>
          </div>
        </div>
      </button>
    );
  }, [selectedIssueId, resolveAssignee]);

  // ── Kanban items (cast to KanbanCardData) ──────────
  const kanbanItems = useMemo(() =>
    issues.map(i => ({ ...i, status: i.status } as Issue & KanbanCardData)),
    [issues],
  );

  // ── Kanban View ────────────────────────────────────
  const kanbanView = (
    <KanbanBoard<Issue & KanbanCardData>
      columns={KANBAN_COLUMNS}
      items={kanbanItems}
      onStatusChange={handleKanbanStatusChange}
      renderCard={renderIssueCard}
      className="min-h-[400px]"
    />
  );

  // ── Dashboard View (collapsible) ────────────────────
  const dashboardView = (
    <div className="mb-4 rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/60">
      {/* Dashboard header with collapse toggle */}
      <button
        type="button"
        onClick={handleToggleDashboard}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50/50 dark:hover:bg-slate-700/20"
      >
        <BarChart3 size={14} className="text-slate-500 dark:text-slate-400" />
        <h3 className="text-[12px] font-bold text-slate-800 dark:text-slate-200">Dashboard de Problemas</h3>
        <ChevronUp size={14} className="ml-auto text-slate-400" />
      </button>

      <div className="border-t border-slate-100 px-4 pb-4 pt-3 dark:border-slate-700/60">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Abertos</p>
            <p className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">{statusCounts.open ?? 0}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Críticos</p>
            <p className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">{severityCounts.critical ?? 0}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Em Andamento</p>
            <p className="mt-1 text-xl font-bold text-amber-600 dark:text-amber-400">
              {(statusCounts.investigation ?? 0) + (statusCounts.root_cause ?? 0) + (statusCounts.action_plan ?? 0) + (statusCounts.execution ?? 0) + (statusCounts.validation ?? 0) + (statusCounts.monitoring ?? 0)}
            </p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Encerrados</p>
            <p className="mt-1 text-xl font-bold text-emerald-600 dark:text-emerald-400">{statusCounts.closed ?? 0}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">MTTR (dias)</p>
            <p className="mt-1 text-xl font-bold text-blue-600 dark:text-blue-400">{avgResolutionDays ?? '—'}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Tempo Médio/Etapa</p>
            <p className="mt-1 text-xl font-bold text-violet-600 dark:text-violet-400">{avgStageTimeDays ?? '—'}<span className="ml-0.5 text-[10px] font-normal text-slate-400">d</span></p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-4">
          {/* Severity breakdown */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Severidade</p>
            <div className="space-y-1.5">
              {(['critical', 'high', 'medium', 'low'] as const).map(sev => {
                const count = severityCounts[sev];
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={sev} className="flex items-center gap-2">
                    <span className="w-16 text-[10px] text-slate-600 dark:text-slate-400">{ISSUE_SEVERITY_LABELS[sev]}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div
                        className={`h-full rounded-full transition-all ${
                          sev === 'critical' ? 'bg-red-500' : sev === 'high' ? 'bg-orange-500' : sev === 'medium' ? 'bg-yellow-500' : 'bg-slate-400'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Status distribution */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Status</p>
            <div className="space-y-1.5">
              {KANBAN_STATUSES.map(s => {
                const count = statusCounts[s] ?? 0;
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <span className="w-20 text-[10px] text-slate-600 dark:text-slate-400">{ISSUE_STATUS_LABELS[s]}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Type */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Tipo</p>
            <div className="space-y-1.5">
              {(Object.keys(ISSUE_TYPE_LABELS) as IssueType[]).map(t => {
                const count = typeCounts[t] ?? 0;
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={t} className="flex items-center gap-2">
                    <span className="w-24 truncate text-[10px] text-slate-600 dark:text-slate-400">{ISSUE_TYPE_LABELS[t]}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Department */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Departamento</p>
            <div className="space-y-1.5">
              {Object.entries(departmentCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([dept, count]) => {
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={dept} className="flex items-center gap-2">
                    <span className="w-24 truncate text-[10px] text-slate-600 dark:text-slate-400">{dept}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Filter Panel removed — filters are now inline in the Ribbon

  return (
    <ModuleLayout ribbonConfig={ribbonWithFilters} headerMeta={problemasHeaderMeta}>
      <div className="flex h-full flex-col">
        {isProjectScopeActive && filterProjectName && (
          <WorkspaceProjectScopeBar
            moduleLabel="Central de Problemas"
            projectName={filterProjectName}
            statusTone={scopeBarTone}
            statusLabel={scopeBarLabel}
            stats={[
              { label: 'Total', value: issues.length },
              { label: 'Abertos', value: scopeStats.openCount, color: scopeStats.openCount > 0 ? 'text-red-500' : undefined },
              { label: 'Críticos', value: scopeStats.criticalCount, color: scopeStats.criticalCount > 0 ? 'text-red-500' : undefined },
            ]}
            onDismiss={handleDismissProjectScope}
            actions={(
              <button
                type="button"
                onClick={handleOpenCreateModal}
                className="flex items-center gap-1 rounded-[3px] bg-blue-600 px-2.5 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700"
              >
                <Plus size={11} /> Problema
              </button>
            )}
          />
        )}

        {/* Main content area */}
        <div ref={contentExportRef} className="flex min-h-0 flex-1 flex-col">
        <ProblemExecutiveStrip
          summary={dashboardData.summary}
          active={quickFilter}
          onChange={setQuickFilter}
        />
        <div className="border-b border-slate-200 bg-white px-3 py-1.5 dark:border-slate-700 dark:bg-slate-900">
          {filtersWidget}
        </div>
        <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-auto">
          <EnterprisePage
            title=""
            subtitle=""
            className={viewMode === 'list' ? '!gap-0 !p-0 bg-slate-100 dark:bg-slate-950' : ''}
          >
            {showDashboard && dashboardView}

            {issuesQuery.isLoading ? (
              <div className="flex items-center justify-center py-20 text-sm text-slate-400">Carregando...</div>
            ) : viewMode === 'kanban' ? (
              kanbanView
            ) : (
              <ProblemCaseList
                issues={issues}
                selectedId={selectedIssueId}
                orgMembers={orgMembers}
                isLoading={issuesQuery.isLoading}
                onOpenWorkspace={(r) => navigate(`/problemas/${r.id}`)}
                emptyTitle={isProjectScopeActive ? 'Nenhum problema para o projeto selecionado' : 'Nenhum problema registrado'}
                emptySubtitle={isProjectScopeActive ? 'Clique em "Ver todos" no ribbon ou no × da barra de contexto para remover o filtro.' : 'Use o botão "Problema" no ribbon para criar'}
              />
            )}
          </EnterprisePage>
        </div>

        {/* Detail Panel */}
        {showDetailPanel && selectedIssue && (
          <ProblemSummaryPanel
            issue={selectedIssue}
            organizationId={organizationId}
            onClose={() => { setShowDetailPanel(false); setSelectedIssueId(null); }}
            onRefresh={handleRefresh}
          />
        )}
        </div>
        </div>
      </div>

      {/* ── Create Issue Modal ────────────────────── */}
      <EnterpriseModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          if (pendingOrigin) clearProblemDraft();
        }}
        title="Novo Problema"
        subtitle="Registrar incidente, não-conformidade ou desvio"
        loading={createLoading}
        footer={
          <>
            <ModalSecondaryButton onClick={() => { setShowCreateModal(false); if (pendingOrigin) clearProblemDraft(); }} disabled={createLoading}>Cancelar</ModalSecondaryButton>
            <ModalPrimaryButton onClick={handleSubmitCreate} loading={createLoading}>Criar Problema</ModalPrimaryButton>
          </>
        }
      >
        {pendingOrigin ? (
          <p className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-100">
            Origem sugerida: {pendingOrigin.label}. Edite antes de salvar. Esta tela ainda não gravou o problema.
          </p>
        ) : null}
        <ModalField label="Título *">
          <input
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Descreva o problema..."
            className={modalInputClass}
            autoFocus
          />
        </ModalField>
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Departamento">
            <input
              value={newDepartment}
              onChange={e => setNewDepartment(e.target.value)}
              placeholder="Ex: Produção, Qualidade..."
              className={modalInputClass}
            />
          </ModalField>
          <ModalField label="Tipo">
            <select value={newType} onChange={e => setNewType(e.target.value as IssueType)} className={modalSelectClass}>
              {(Object.entries(ISSUE_TYPE_LABELS) as [IssueType, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </ModalField>
        </div>
        <ModalField label="Severidade">
          <select value={newSeverity} onChange={e => setNewSeverity(e.target.value as IssueSeverity)} className={modalSelectClass}>
            {(Object.entries(ISSUE_SEVERITY_LABELS) as [IssueSeverity, string][]).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </ModalField>
        <label className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 px-3 py-2 dark:border-slate-700">
          <input
            type="checkbox"
            checked={forceStructured}
            onChange={e => setForceStructured(e.target.checked)}
            className="rounded border-slate-300"
          />
          <span className="text-[11px] text-slate-600 dark:text-slate-400">
            Abrir como estruturado (MASP completo)
          </span>
        </label>
        {!forceStructured && (
          <p className="text-[10px] text-slate-500 dark:text-slate-400">
            Modo automático: incidentes → correção rápida; NC, reclamações, críticos e recorrentes → estruturado.
          </p>
        )}
        {forceStructured && (
          <div className="rounded border border-orange-200 bg-orange-50 px-3 py-2 dark:border-orange-800/40 dark:bg-orange-900/20">
            <p className="text-[10px] font-medium text-orange-700 dark:text-orange-400">
              <Crosshair size={10} className="mr-1 inline" />
              Problema Estruturado (MASP): 8 etapas — Investigação → Causa Raiz → Plano → Execução → Validação → Monitoramento
            </p>
          </div>
        )}
        <ModalField label="Descrição">
          <textarea
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            onBlur={handleCheckRecurrence}
            placeholder="Detalhes, contexto, evidências..."
            className={`${modalInputClass} min-h-[80px] resize-y`}
            rows={3}
          />
        </ModalField>

        {/* Recurrence warnings */}
        {recurrences.length > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-900/20">
            <p className="mb-1.5 text-[10px] font-bold text-amber-700 dark:text-amber-400">
              ⚠ Possível recorrência detectada
            </p>
            <div className="space-y-1">
              {recurrences.map(match => (
                <div key={match.issue_id} className="flex items-center gap-2 text-[10px] text-amber-600 dark:text-amber-400/80">
                  <span className="font-medium">{Math.round(match.similarity_score * 100)}%</span>
                  <span className="truncate">{match.issue_title}</span>
                  <span className="ml-auto shrink-0 rounded bg-amber-200 px-1 py-0.5 text-[8px] font-semibold dark:bg-amber-800/40">
                    {match.matching_fields.join(', ')}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-1 text-[9px] text-amber-500 dark:text-amber-500">
              Verifique se este não é um problema recorrente antes de criar novo registro.
            </p>
          </div>
        )}
      </EnterpriseModal>

      <ProblemReasonModal
        open={reasonAsk != null}
        mode={reasonAsk?.mode ?? 'close'}
        onClose={() => setReasonAsk(null)}
        onConfirm={confirmReasonAsk}
      />

      <ToastContainer />
    </ModuleLayout>
  );
};
